import { useState, useMemo, useEffect } from "react";
import { Table, Input, Select, Space, Tag, Typography, DatePicker, Button, message, Tooltip } from "antd";
import { TbSearch, TbRefresh, TbFileText, TbAlertCircle, TbCircleCheck, TbCircleX } from "react-icons/tb";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useParams } from "react-router-dom";
import DashboardLayout from "layout/DashboardLayout";
import JobStatsCharts from "./components/JobStatsCharts";
import { ApiService } from "api/api.service";
import { useDebounce } from "use-debounce";
import PageTitle from "components/PageTitle";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export interface BackgroundLog {
  _id: string;
  job_type: string;
  job_id: string;
  timestamp: string;
  message: string;
  stack_trace: string | null;
  metadata: {
    appkey: string;
    celery_task_id: string;
    source_uri: string;
  };
}

function BackgroundJobLogs() {
  const { appkey } = useParams<{ appkey: string }>();

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch] = useDebounce(searchText, 400);
  const [jobTypeFilter, setJobTypeFilter] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [pendingLogs, setPendingLogs] = useState(0);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, jobTypeFilter, statusFilter, dateRange]);

  const params = {
    page,
    limit: pageSize,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(jobTypeFilter && { job_type: jobTypeFilter }),
    ...(statusFilter && { status: statusFilter }),
    ...(dateRange?.[0] && { start_date: dateRange[0].toISOString() }),
    ...(dateRange?.[1] && { end_date: dateRange[1].toISOString() }),
  };

  const { data, isLoading, refetch } = ApiService.application.useGetBackgroundLogs(appkey, params);

  const logs: BackgroundLog[] = data?.result.data ?? [];
  const total = data?.result.totalObjects ?? 0;

  useEffect(() => {
    const source = new EventSource(`${import.meta.env.VITE_APP_API_URL}/background-jobs/events`);

    source.addEventListener("refetch-logs", () => {
      setPendingLogs((prev) => prev + 1);
    });

    return () => {
      source.close();
    };
  }, []);

  const jobTypes = [
    { label: "Forecast", value: "forecast" },
    { label: "Debtor Summary", value: "debtor_summary" },
    { label: "FIA", value: "fia" },
    { label: "MoneyThumb", value: "moneythumb" },
  ];

  const getStatusTag = (log: BackgroundLog) => {
    const isError =
      log.stack_trace !== null ||
      log.message.toLowerCase().includes("error") ||
      log.message.toLowerCase().includes("fail");

    const isSuccess = !log.stack_trace && log.message.toLowerCase().includes("success");

    if (isError) {
      return (
        <Tag icon={<TbCircleX size={14} />} color="error">
          Failed
        </Tag>
      );
    }

    if (isSuccess) {
      return (
        <Tag icon={<TbCircleCheck size={14} />} color="success">
          Success
        </Tag>
      );
    }

    return (
      <Tag icon={<TbAlertCircle size={14} />} color="default">
        Info
      </Tag>
    );
  };

  const columns: ColumnsType<BackgroundLog> = [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      defaultSortOrder: "descend",
      render: (date: string) => (
        <div className="flex flex-col">
          <Text className="text-xs font-medium">{dayjs(date).format("MMM DD, YYYY")}</Text>
          <Text className="text-xs text-gray-500">{dayjs(date).format("HH:mm:ss")}</Text>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, record) => getStatusTag(record),
    },
    {
      title: "Job Type",
      dataIndex: "job_type",
      key: "job_type",
      width: 140,
      render: (type: string) => (
        <Tag color="blue" className="capitalize">
          {type}
        </Tag>
      ),
    },
    {
      title: "Job ID",
      dataIndex: "job_id",
      key: "job_id",
      width: 280,
      render: (id: string) => (
        <Text className="text-xs font-mono text-gray-600" copyable>
          {id}
        </Text>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text}</span>
        </Tooltip>
      ),
    },
    // {
    //   title: "App Key",
    //   dataIndex: ["metadata", "appkey"],
    //   key: "appkey",
    //   width: 140,
    //   render: (key: string) => <Text className="text-xs font-mono text-gray-600">{key}</Text>,
    // },
  ];

  const expandedRowRender = (record: BackgroundLog) => (
    <div className="px-6 py-4 bg-gray-50 border-t space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <Text className="text-gray-500 block mb-1">Celery Task ID</Text>
          <div className="font-mono text-xs break-all">{record.metadata?.celery_task_id ?? "—"}</div>
        </div>

        <div>
          <Text className="text-gray-500 block mb-1">Source URI</Text>
          <div className="font-mono text-xs break-all">{record.metadata?.source_uri ?? "—"}</div>
        </div>
      </div>

      {record.stack_trace && (
        <div>
          <Text className="text-gray-500 block mb-2">Stack Trace</Text>

          <pre className="text-xs bg-red-50 border border-red-200 rounded p-3 overflow-x-auto text-red-800 font-mono whitespace-pre-wrap">
            {typeof record.stack_trace === "string" ? record.stack_trace : JSON.stringify(record.stack_trace, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <PageTitle
        title={"Application Background Worker Logs"}
        pageTitle={[
          { title: "Applications", link: "/dashboard/applications" },
          { title: appkey ?? "Application", link: `/dashboard/application/${appkey}` },
        ]}
      />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TbFileText size={22} className="text-blue-600" />
            <Title level={4} className="!mb-0">
              Background Job Logs
            </Title>
          </div>

          <Button icon={<TbRefresh size={16} />} onClick={() => refetch()}>
            Refresh
          </Button>
        </div>

        <Space direction="vertical" size="middle" className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
            <Input
              placeholder="Search logs"
              prefix={<TbSearch size={16} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />

            <Select
              placeholder="Job Type"
              value={jobTypeFilter}
              onChange={setJobTypeFilter}
              allowClear
              options={jobTypes}
              className="w-full"
            />

            <Select
              className="w-full"
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              options={[
                { label: "Success", value: "success" },
                { label: "Failed", value: "error" },
              ]}
            />

            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
              needConfirm={false}
              className="w-full"
            />
          </div>

          {pendingLogs > 0 && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded px-4 py-2">
              <Text className="text-blue-700 text-sm">
                {pendingLogs} new log{pendingLogs > 1 ? "s" : ""} arrived
              </Text>

              <Button
                type="primary"
                size="small"
                onClick={() => {
                  refetch();
                  setPendingLogs(0);
                }}
              >
                Refetch
              </Button>
            </div>
          )}

          <Table
            columns={columns}
            dataSource={logs}
            loading={isLoading}
            rowKey="_id"
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => !!record.metadata,
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: false,
              onChange: (p) => setPage(p),
            }}
            scroll={{ x: 1000 }}
          />
        </Space>

        <div className="pt-6 border-t">
          <Title level={5} className="!mb-4">
            Job Analytics
          </Title>

          <JobStatsCharts logs={logs} />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default BackgroundJobLogs;
